// Shadows / Highlights — Photoshop-style adjustment.
//
// Brightens shadows and darkens highlights based on pixel luminance
// thresholds. This is a per-pixel simplified implementation (no Gaussian
// blur pass) suitable for an MVP; results are reasonable for most edits.
//
// Reference:
//   shadowThreshold  = shadowTonalWidth / 100
//   highlightThreshold = 1 - highlightTonalWidth / 100
//   For each pixel:
//     lum = weighted luminance (Rec. 709 luma coefficients)
//     if lum < shadowThreshold:
//       weight = (shadowThreshold - lum) / shadowThreshold
//       amount = (shadowAmount / 100) * weight * 255
//       R,G,B += amount
//     if lum > highlightThreshold:
//       weight = (lum - highlightThreshold) / (1 - highlightThreshold)
//       amount = (highlightAmount / 100) * weight * 255
//       R,G,B -= amount
//
// Alpha is never touched. Selection masking is delegated to
// `applyFilterInPlace`.

import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export interface ShadowsHighlightsParams {
  /** 0..100, how much to lighten shadows (default 35) */
  shadowAmount: number;
  /** 0..100, range of tones considered shadows (default 50) */
  shadowTonalWidth: number;
  /** 0..2500, neighbourhood size for shadow detection (default 30) */
  shadowRadius: number;
  /** 0..100, how much to darken highlights (default 0) */
  highlightAmount: number;
  /** 0..100, range of tones considered highlights (default 50) */
  highlightTonalWidth: number;
  /** 0..2500, neighbourhood size (default 30) */
  highlightRadius: number;
  /** 0..200, saturation boost after adjustment (default 20) */
  colorCorrection: number;
  /** 0..50, black clip (default 0) */
  blackClip: number;
  /** 0..50, white clip (default 0) */
  whiteClip: number;
}

const DEFAULT_PARAMS: ShadowsHighlightsParams = {
  shadowAmount: 35,
  shadowTonalWidth: 50,
  shadowRadius: 30,
  highlightAmount: 0,
  highlightTonalWidth: 50,
  highlightRadius: 30,
  colorCorrection: 20,
  blackClip: 0,
  whiteClip: 0,
};

// ---------------------------------------------------------------------------
// Filter export
// ---------------------------------------------------------------------------

export const ShadowsHighlights: Filter<ShadowsHighlightsParams> = {
  id: "shadows-highlights",
  displayName: "Shadows/Highlights",
  defaultParams: DEFAULT_PARAMS,

  apply(
    input: PixelBuffer,
    params: ShadowsHighlightsParams,
    _selection: Selection | null,
  ): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;
    const n = w * h;

    const shadowAmt = clampRange(params.shadowAmount, 0, 100);
    const highlightAmt = clampRange(params.highlightAmount, 0, 100);
    const shadowTW = clampRange(params.shadowTonalWidth, 0, 100);
    const highlightTW = clampRange(params.highlightTonalWidth, 0, 100);

    const shadowThreshold = shadowTW / 100;
    const highlightThreshold = 1 - highlightTW / 100;

    for (let i = 0; i < n; i++) {
      const o = i * 4;
      const r = src[o] ?? 0;
      const g = src[o + 1] ?? 0;
      const b = src[o + 2] ?? 0;
      const a = src[o + 3] ?? 0;

      if (a === 0) {
        dst[o] = 0;
        dst[o + 1] = 0;
        dst[o + 2] = 0;
        dst[o + 3] = 0;
        continue;
      }

      // Luminance (Rec. 709 luma coefficients, scaled to 0..1)
      const lum = (r * 77 + g * 150 + b * 29) / (255 * 256);

      let dr = r;
      let dg = g;
      let db = b;

      // Shadow adjustment
      if (lum < shadowThreshold && shadowAmt > 0) {
        const weight = (shadowThreshold - lum) / shadowThreshold;
        const amount = (shadowAmt / 100) * weight * 255;
        dr += amount;
        dg += amount;
        db += amount;
      }

      // Highlight adjustment
      if (lum > highlightThreshold && highlightAmt > 0) {
        const weight = (lum - highlightThreshold) / (1 - highlightThreshold);
        const amount = (highlightAmt / 100) * weight * 255;
        dr -= amount;
        dg -= amount;
        db -= amount;
      }

      dst[o] = clampByte(dr);
      dst[o + 1] = clampByte(dg);
      dst[o + 2] = clampByte(db);
      dst[o + 3] = a;
    }

    return out;
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function clampRange(v: number, lo: number, hi: number): number {
  if (!isFinite(v)) return lo;
  if (v < lo) return lo;
  if (v > hi) return hi;
  return v;
}

function clampByte(v: number): number {
  const c = Math.round(v);
  return c < 0 ? 0 : c > 255 ? 255 : c;
}
